import { getAuth, signInAnonymously } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

export async function ensureInviteAuth(invitationToken: string): Promise<void> {
  const auth = getAuth();
  
  // If auth already has a current user, proceed immediately without waiting
  if (!auth.currentUser) {
    // Wait for auth initialization - Firebase v9+ fires onAuthStateChanged when ready
    console.log("ensureInviteAuth: Waiting for auth initialization...");
    await new Promise<void>((resolve) => {
      const unsubscribe = auth.onAuthStateChanged(() => {
        unsubscribe();
        resolve();
      });
    });

    // Sign in anonymously if no user is present after initialization
    if (!auth.currentUser) {
      console.log("ensureInviteAuth: Signing in anonymously...");
      await signInAnonymously(auth);
    }
  }
  
  console.log("ensureInviteAuth: Calling grantInviteClaims function...");
  const functions = getFunctions(undefined, "us-east4");
  const grant = httpsCallable(functions, "grantInviteClaims");
  
  try {
    await grant({ token: invitationToken });
    console.log("ensureInviteAuth: Successfully granted invitation claims");
    
    // Force refresh the ID token to get the new custom claims
    await auth.currentUser?.getIdToken(true);
    console.log("ensureInviteAuth: ID token refreshed with new claims");
  } catch (error) {
    console.error("ensureInviteAuth: Failed to grant invitation claims:", error);
    throw error;
  }
}

const CLAIMS_RETRY_DELAY_MS = 750;

async function tokenCarriesInviteClaims(): Promise<boolean> {
  const user = getAuth().currentUser;
  if (!user) return false;
  const { claims } = await user.getIdTokenResult(true);
  return Boolean(claims.role && claims.organizationId);
}

/**
 * KB-3969: claim the invitation AND confirm the claims are on the ID token, before the
 * welcome page lists any camp data. kb-ff's rules deny an unlimited list on campLocations /
 * campOrganizations to a caller without a staff `role` claim, so listing before this resolves
 * renders "No locations found" to a brand-new invitee. The first refresh after
 * setCustomUserClaims can come back without the new claims (the KB-136 race), hence one retry.
 * Throws when the claim fails or the claims never appear, so the caller can say so.
 */
export async function ensureInviteClaimsReady(invitationToken: string): Promise<void> {
  await ensureInviteAuth(invitationToken);
  if (await tokenCarriesInviteClaims()) return;
  await new Promise((resolve) => setTimeout(resolve, CLAIMS_RETRY_DELAY_MS));
  if (await tokenCarriesInviteClaims()) return;
  throw new Error("ensureInviteClaimsReady: invitation claims missing from the ID token after refresh");
}

export async function persistInviteToAccountIfNeeded(): Promise<void> {
  const token = localStorage.getItem("kb_invite_token"); // SECURITY_AUDIT_PASS: localStorage key name only, not a secret
  if (!token) return;
  const auth = getAuth();
  if (!auth.currentUser || auth.currentUser.isAnonymous) return;
  const functions = getFunctions(undefined, "us-east4");
  const persist = httpsCallable(functions, "persistInviteToAccount");
  await persist({ token });
  localStorage.removeItem("kb_invite_token");
  await auth.currentUser.getIdToken(true);
}

/**
 * Persist invitation to the signed-in (non-anonymous) user using an explicit token
 * (e.g. welcome page URL token). Does not read or clear localStorage.
 */
export async function persistInviteToAccountWithToken(invitationToken: string): Promise<void> {
  const token = String(invitationToken ?? "").trim();
  if (!token) return;

  const auth = getAuth();
  if (!auth.currentUser || auth.currentUser.isAnonymous) {
    throw new Error("persistInviteToAccountWithToken: signed-in non-anonymous user required");
  }

  const functions = getFunctions(undefined, "us-east4");
  const persist = httpsCallable(functions, "persistInviteToAccount");
  await persist({ token });
  await auth.currentUser.getIdToken(true);
}


